import {
  castToBoolean,
  normalizeSingleDayStartEndDates,
  normalizeIdentifier,
} from '../util';

/**
 * Position; represents a record from events_staffing_days_positions table
 */
class Position {
  constructor(data) {
    data = data || {};

    this.id = data.id;

    this.start_date = data.start_date;
    this.start_time = data.start_time;
    this.end_time   = data.end_time;
    this.claim_date = data.claim_date;

    this.actual_end                  = data.actual_end;
    this.actual_hours                = data.actual_hours;
    this.actual_start                = data.actual_start;
    this.allow_event_checkin         = data.allow_event_checkin;
    this.allow_no_location           = castToBoolean(data.allow_no_location);
    this.checkin                     = data.checkin;
    this.children                    = data.children;
    this.city                        = data.city;
    this.claim_types                 = data.claim_types || null;
    this.claim_types_id              = data.claim_types_id;
    this.client_name                 = data.client_name;
    this.clients_id                  = data.clients_id;
    this.compliance_enabled          = data.compliance_enabled;
    // CORE1-8103: merch-visit non-compliance flags (per-position, from the API).
    // The model whitelists fields, so these must be copied explicitly or the
    // shift-card button never sees them.
    this.non_compliance_enabled      = data.non_compliance_enabled;
    this.non_compliant               = data.non_compliant;
    this.created_at                  = data.created_at;
    this.current_checkin_status      = data.current_checkin_status;
    this.customer_rate               = data.customer_rate;
    this.customer_total              = data.customer_total;
    this.disable_add_photos          = castToBoolean(data.disable_add_photos) ?? false;
    this.documents                   = data.documents;
    this.event                       = data.event;
    this.events_id                   = data.events_id;
    this.events_name                 = data.events_name;
    this.event_type                  = data.event_type;
    this.events_staffing_days_id     = data.events_staffing_days_id;
    this.guerilla_stops              = data.guerilla_stops;
    this.hours_confirmed_time        = data.hours_confirmed_time;
    this.last_checkin_activity       = data.last_checkin_activity;
    this.last_pending_alert          = data.last_pending_alert;
    this.last_pending_alert_time     = data.last_pending_alert_time;
    this.late_checkin_alert_sent     = data.late_checkin_alert_sent;
    this.market_name                 = data.market_name;
    this.markets_id                  = data.markets_id;
    this.meal_break_duration_mins    = data.meal_break_duration_mins || null;
    this.meal_break_eligible         = typeof data.meal_break_eligible !== 'undefined' ? data.meal_break_eligible :  null;
    this.meal_break_qty              = data.meal_break_qty || null;
    this.meal_break_end              = data.meal_break_end;
    this.meal_break_threshold_hours  = data.meal_break_threshold_hours || null;
    this.meal_break_start            = data.meal_break_start;
    this.meal_break_ack_options      = data.meal_break_ack_options || {}
    this.meal_break_acknowledgement  = data.meal_break_acknowledgement
    this.original_payroll_periods_id = data.original_payroll_periods_id;
    this.other_users_positions       = data.other_users_positions;
    this.pay_rate                    = data.pay_rate;
    this.forecast_rate               = data.forecast_rate || 0;
    this.payroll_periods_id          = data.payroll_periods_id;
    this.positionType                = data.positionType || null;
    this.position_id                 = data.position_id;
    this.position_name               = data.position_name;
    this.primaryClaim                = data.primaryClaim || null;
    this.programs_id                 = data.programs_id;
    this.programs_name               = data.programs_name;
    this.rating                      = data.rating;
    this.real_events_id              = data.real_events_id;
    this.recapResponse               = data.recapResponse || null;
    this.recap_on_portal             = data.recap_on_portal;
    this.reminder_response           = data.reminder_response;
    this.reminder_sent               = data.reminder_sent;

    /** Compliance Rest Break Properties **/
    this.rest_break_eligible         = typeof data.rest_break_eligible !== 'undefined' ? data.rest_break_eligible :  null
    this.rest_break_threshold_hours  = data.rest_break_threshold_hours || null
    this.rest_break_interval_hours   = data.rest_break_interval_hours || null
    this.rest_break_duration_mins    = data.rest_break_duration_mins || null
    this.rest_break_expected_qty     = data.rest_break_expected_qty || null
    this.rest_break_ack_options      = data.rest_break_ack_options || {}
    this.rest_break_acknowledgement  = data.rest_break_acknowledgement
    this.rest_break_start            = {}
    this.rest_break_end              = {}

    // Add properties for expected number of rest breaks (rest_break_start and rest_break_end)
    if (this.rest_break_eligible && this.rest_break_expected_qty) {
      for (let i = 1; i <= this.rest_break_expected_qty; i++) {
        this.rest_break_start[i] = data.rest_break_start[i] || null
        this.rest_break_end[i]   = data.rest_break_end[i] || null
      }
    }

    this.rsvp_send_time              = data.rsvp_send_time;
    this.rsvp_sent                   = data.rsvp_sent;
    this.staffing_days_types_id      = data.staffing_days_types_id;
    this.staffing_positions_id       = data.staffing_positions_id;
    this.staffing_statuses_id        = data.staffing_statuses_id;
    this.staffing_users_first_name   = data.staffing_users_first_name;
    this.staffing_users_id           = data.staffing_users_id;
    this.staffing_users_last_name    = data.staffing_users_last_name;
    this.state                       = data.state;
    this.status                      = data.status;
    this.street_address1             = data.street_address1;
    this.street_address2             = data.street_address2;
    this.total_hours                 = data.total_hours;
    this.total_pay                   = data.total_pay;
    this.unlocked_categories         = data.unlocked_categories;
    this.updated_at                  = data.updated_at;
    this.variance                    = data.variance;
    this.venue_name                  = data.venue_name;
    this.view_documents_on_portal    = data.view_documents_on_portal;
    this.vpid                        = data.vpid;
    this.zip                         = data.zip;
    this.shiftSegment                = data.shiftSegment || null;
    this.shift_segments_id           = data.shift_segments_id || null;
    this.is_eds                      = castToBoolean(data.is_eds);
    this.is_available                = castToBoolean(data.is_available);
    this.self_staff                  = castToBoolean(data.self_staff);
    this.venues_custom               = data.venues_custom;
    this.allow_claims                = castToBoolean(data.allow_claims);
    this.allow_before_date_days      = parseInt(data.allow_before_date_days);
    this.guerrilla_stops_use_parent_date = castToBoolean(data.guerrilla_stops_use_parent_date);
    this.guerrilla_stops_use_parent_recap_definition = castToBoolean(data.guerrilla_stops_use_parent_recap_definition);
    this.event.created_from_request  = castToBoolean(data.event.created_from_request);
    // Client-level toggle (Recaps > "Show Rate on Visit Details"). Defaults to
    // showing the rate when the flag is absent (e.g. offline-cached positions
    // serialized before this toggle existed), preserving prior behavior.
    this.show_rate                   = typeof data.app_show_rate !== 'undefined' ? castToBoolean(data.app_show_rate) : true;
    this.show_requester_name         = data.app_show_requester_name;
    this.show_requester_phone        = data.app_show_requester_phone;
    this.show_requester_email        = data.app_show_requester_email;
    this.show_request_date           = data.app_show_request_date;
    this.requester_phone             = data.requester_phone;
    this.requester_first_name        = data.requester_first_name;
    this.requester_last_name         = data.requester_last_name;
    this.requester_email             = data.requester_email;
    this.requested_at                = data.requested_at;
    this.request_organization        = data.request_organization;
    this.request_images              = data.request_images || [];
  }

  get canRecap() {
    let ret;
    if (!this.event || !this.event.events_recaps_id) {
      // No recap definition linked
      ret = false;
    } else if ((this.isFuture) && (!this.canSubmitEarlyRecap)){
      // Don't allow recapping of future positions
      ret = false;
    } else if (!castToBoolean(this.recap_on_portal)) {
      // Don't allow if "can recap on portal?" is false
      ret = false;
    } else if (castToBoolean(this.event.recaps_locked)) {
      // Don't allow if recaps locked on event
      ret = false;
    } else if (this.recapResponse && this.recapResponse.isApproved) {
      // Don't allow editing of recap response if status is approved
      ret = false;
    } else if (this.event.isApprovedRecapped()) {
      // Don't allow recapping if the event already has an approved recap
      // This can happen if more than one person is staffing an event, and someone else has already recapped it
      ret = false;
    } else if (this.event.isLastMinuteCancelled()) {
      // Don't allow recapping if the event has been cancelled
      ret = false;
    } else if (
      this.recapResponse
      && this.recapResponse.recapDefinition
      && !this.recapResponse.recapDefinition.dateIsWithinAvailableOffsets(new Date())
    ) {
      // Don't allow editing if current date is outside of availability offsets
      ret = false;
    } else {
      // All else looks good; allow recap
      ret = true;
    }
    return ret;
  }

  /**
   * Whether the office has rejected THIS staff member's recap for this shift.
   *
   * The recap response is this staffer's own record and is authoritative whenever it is present:
   * if it says the recap was not rejected, an event-row flag cannot override it, because the
   * event row is shared by everyone on the event and its rejection may concern another staffer
   * or a legacy office action. Only when the staffer's own response is absent - which is the
   * reported defect, since it lives in a window-limited store whose load failure is swallowed -
   * does the event row stand in for it.
   *
   * THE FALLBACK IS NOT PROVENANCE, and must not be read as it. The event row has two writers:
   * this mechanism's mirror, which only writes on single-staffer events, and the legacy per-event
   * Reject Recap button (EventRecapController::rejectRecapCallback()), which writes the same
   * columns on an event of any size and is deliberately exempt from the server's clear. So on a
   * multi-staffer event the fallback can reflect a rejection that is not this staffer's. It is
   * used where showing an extra notice is better than showing none - never to decide whether a
   * control is locked, which is what ownRecapRejected is for.
   */
  get recapRejected() {
    if (this.recapResponse) {
      return Boolean(this.recapResponse.isRejected);
    }
    // An unclaimed shift has nobody's recap on it, so it has no rejection to report. Without this
    // an available position - which never has a recapResponse of its own - would take the event-row
    // fallback unconditionally. An event with one staffer and one unfilled slot still counts as
    // single-staffer server-side, because hasAtMostOneStaffMemberOnTheEvent() deliberately does not
    // count a NULL staffing_users_id, so the mirror does write that row: the open slot advertised
    // to every other staff member would otherwise carry the office's decline reason about the
    // person who did work it.
    if (this.isAvailable) {
      return false;
    }
    return Boolean(this.event && this.event.isRecapRejected());
  }

  get canSubmitEarlyRecap() {
     let futureDate = new Date();
     futureDate.setDate(futureDate.getDate() + this.allow_before_date_days);
     return !this.isFutureRelativeTo(futureDate);
  }

  get canSubmitClaims() {
    return this.canSubmitPrimaryClaim || this.canSubmitSecondaryClaims;
  }

  get canSubmitPrimaryClaim() {
    let ret;
    if (this.isFuture) {
      ret = false;
    } else if (this.event.claims_locked) {
      ret = false;
    } else {
      ret = true;
    }
    return ret;
  }

  get canSubmitSecondaryClaims() {
    let ret;
    if (this.isFuture) {
      ret = false;
    } else if (this.positionType && !castToBoolean(this.positionType.claims_on_portal)) {
      ret = false;
    } else if (this.event.claims_locked) {
      ret = false;
    } else {
      ret = true;
    }
    return ret;
  }

  staffUserCanEditClaims(staffUser) {
    let result;
    const eventId = this.events_id;

    if (!this.canSubmitClaims) {
      return false;
    }

    if (staffUser.getClaimsForEventId(eventId).length > 0) {
      result = true;
    } else if (this.enqueuedPrimaryClaim) {
      result = true;
    } else if (this.event && this.event.enqueuedSecondaryClaims && this.event.enqueuedSecondaryClaims.length > 0) {
      result = true;
    } else {
      result = false;
    }

    return result;
  }

  get hasRecap() {
    return !!this.recapResponse;
  }

  get canSubmitPrimaryClaimsForOtherUsers() {
    let ret = false;
    if (this.positionType) {
      if (castToBoolean(this.positionType.claims_on_portal_others)) {
        ret = true;
      }
    }
    return ret;
  }

  get claimTypes() {
    if (this.event && this.event.claimTypes) {
      return this.event.claimTypes.filter(claimType => claimType.position_access.includes(parseInt(this.id)));
    }
    else {
      return [];
    }
  }

  get primaryClaimTypes() {
    if (this.event && this.event.primaryClaimTypes) {
      return this.event.primaryClaimTypes.filter(claimType => claimType.position_access.includes(parseInt(this.id)));
    }
    else {
      return [];
    }
  }

  get canAddGuerillaStop() {
    return !this.isFuture && castToBoolean(this.guerilla_stops) && castToBoolean(this.recap_on_portal);
  }

  get isPast() {
    return this.isPastRelativeTo(new Date());
  }

  get isCurrent() {
    return this.isCurrentRelativeTo(new Date());
  }

  get isFuture() {
    return this.isFutureRelativeTo(new Date());
  }

  get isAvailable() {
    return this.is_available === true;
  }

  get recapDefinition() {
    return this.event && this.event.recapDefinition;
  }

  get isCheckedOut() {
    return this.current_checkin_status === 'out'
  }

  get eventId() {
    if (this.event) {
      return normalizeIdentifier(this.event.id);
    } else if (this.events_id) {
      return normalizeIdentifier(this.events_id);
    } else if (this.real_events_id) {
      return normalizeIdentifier(this.real_events_id);
    } else {
      return null;
    }
  }

  get isLockedPostRecap() {
    if (!castToBoolean(this.event.prompt_recap_checkout_claim)) {
      return false
    }

    const hasResponse = !!this.recapResponse

    if (!hasResponse) {
      return false
    }

    return true
  }

  get canEdit() {
    return this.unlocked_categories && this.unlocked_categories.length > 0;
  }

  claimTypesForCategoryId(claimCategoryId) {
    if (this.event && this.event.claimTypesForCategoryId(claimCategoryId)) {
      return this.event.claimTypesForCategoryId(claimCategoryId).filter(claimType => claimType.position_access.includes(parseInt(this.id)));
    }
    else {
      return [];
    }
  }

  claimTypesForCategory(claimCategory) {
    if (this.event && this.event.claimTypesForCategory(claimCategory)) {
      return this.event.claimTypesForCategory(claimCategory).filter(claimType => claimType.position_access.includes(parseInt(this.id)));
    }
    else {
      return [];
    }
  }

  isPastRelativeTo(relativeToDate) {
    // TODO: Ensure that this.start_time is always a Date object!
    return this.compareDatesAtMidnight(this.start_time, relativeToDate) === -1;
  }

  isCurrentRelativeTo(relativeToDate) {
    // TODO: Ensure that this.start_time is always a Date object!
    return this.compareDatesAtMidnight(this.start_time, relativeToDate) === 0;
  }

  isFutureRelativeTo(relativeToDate) {
    // TODO: Ensure that this.start_time is always a Date object!
    return this.compareDatesAtMidnight(this.start_time, relativeToDate) === 1;
  }

  compareDatesAtMidnight(date1, date2) {
    let ts1 = new Date(date1.getTime()).setHours(0, 0, 0, 0);
    let ts2 = new Date(date2.getTime()).setHours(0, 0, 0, 0);
    if (ts1 < ts2) {
      return -1;
    } else if (ts1 > ts2) {
      return 1;
    } else {
      return 0;
    }
  }

  updateActualHours() {
    let [startDate, endDate] = normalizeSingleDayStartEndDates(this.actual_start, this.actual_end);
    let startTime = startDate && startDate.getTime();
    let endTime = endDate && endDate.getTime();

    if (startTime && endTime) {
      const duration = endTime - startTime;
      const secs = duration / 1000.0;
      this.actual_hours = secs / 3600.0;
    }
    if (!startTime || !endTime) {
      this.actual_hours = 0;
    }
  }

  toggleCheckinStatus() {
    if (this.current_checkin_status === 'in') {
      this.current_checkin_status = 'out';
    } else {
      this.current_checkin_status = 'in';
    }
  }

  pushLocalCheckin(checkin) {
    this.checkin.push(checkin)
  }
}

export default Position;
